import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useQueryClient } from '@tanstack/react-query';

import { useAuth } from '@saas-kit/lib';

import { Drawer } from '../Drawer';
import { Tooltip } from '../Tooltip';
import { cn } from '../cn';

import { BellIcon } from './icons';
import {
  NotificationsDrawerContent,
  UNREAD_QUERY_KEY,
} from './NotificationsDrawerContent';
import { fetchUnreadCount } from './notifications-api';

const POLL_INTERVAL_MS = 30_000;

interface Props {
  to: string;
}

export function NotificationsBell({ to }: Props): JSX.Element | null {
  const { t } = useTranslation('common');
  const authed = useAuth((s) => s.user !== null);
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);

  const countQuery = useQuery<number>({
    queryKey: UNREAD_QUERY_KEY,
    queryFn: fetchUnreadCount,
    enabled: authed,
    refetchInterval: authed ? POLL_INTERVAL_MS : false,
    staleTime: 10_000,
  });

  useEffect(() => {
    if (!authed) return;
    const handler = (): void => {
      if (document.visibilityState === 'visible') {
        void qc.invalidateQueries({ queryKey: UNREAD_QUERY_KEY });
      }
    };
    document.addEventListener('visibilitychange', handler);
    return () => document.removeEventListener('visibilitychange', handler);
  }, [authed, qc]);

  if (!authed) return null;

  const count = countQuery.data ?? 0;
  const display = count > 99 ? '99+' : String(count);

  return (
    <>
      <Tooltip label={t('nav.notifications')}>
        <button
          type="button"
          aria-label={t('nav.notifications_aria')}
          data-testid="notifications-trigger"
          data-unread={count > 0 ? 'true' : 'false'}
          onClick={() => setOpen(true)}
          className="relative inline-flex h-9 w-9 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
        >
          <BellIcon size={18} />
          {count > 0 ? (
            <span
              data-testid="notifications-badge"
              className={cn(
                'absolute -end-0.5 -top-0.5 inline-flex min-w-[1.1rem] items-center justify-center rounded-full bg-primary px-1 text-[10px] font-semibold leading-tight text-primary-foreground',
              )}
            >
              {display}
            </span>
          ) : null}
        </button>
      </Tooltip>
      <Drawer
        open={open}
        onOpenChange={setOpen}
        title={t('nav.notifications')}
        testId="notifications-drawer"
        width="md"
      >
        <NotificationsDrawerContent
          open={open}
          notificationsTo={to}
          onClose={() => setOpen(false)}
        />
      </Drawer>
    </>
  );
}
